<template>
  <div class="w-full overflow-x-hidden">
    <header
      class="nav-header fixed w-full top-0 z-50 transition-all duration-300"
      :class="{ scrolled: scrolled }"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6">
        <div class="flex justify-between items-center h-20">
          <!-- Logo with Brand Name -->
          <div class="flex items-center space-x-3">
            <router-link to="/" class="flex items-center space-x-5 group">
              <div class="logo-container relative">
                <div class="logo-glow"></div>
                <img
                  :src="require('@/assets/logo1.png')"
                  alt="Youth Ally Logo"
                  class="logo-image h-12 w-auto object-contain rounded-xl transition-all duration-500"
                />
              </div>
              <div class="brand-text hidden lg:block">
                <div class="text-logo-color font-bold text-xl tracking-tight">
                  Youth Ally
                </div>
              </div>
            </router-link>
          </div>

          <!-- Desktop Navigation -->
          <nav class="hidden md:flex items-center space-x-2">
            <router-link
              v-for="item in navItems"
              :key="item.path"
              :to="item.path"
              class="nav-link relative px-5 py-2.5 rounded-xl transition-all duration-300 group"
            >
              <!-- Background Effects -->
              <div
                class="absolute inset-0 rounded-xl transition-all duration-300"
                :class="
                  isActiveRoute(item.path)
                    ? 'bg-white shadow-lg'
                    : 'bg-transparent group-hover:bg-logo-color/10'
                "
              ></div>

              <!-- Content -->
              <div class="relative flex items-center space-x-2">
                <font-awesome-icon
                  :icon="item.icon"
                  class="w-4 h-4 transition-all duration-300"
                  :class="
                    isActiveRoute(item.path)
                      ? 'text-logo-color'
                      : 'text-logo-color/70 group-hover:text-logo-color'
                  "
                />
                <span
                  class="text-sm font-semibold transition-all duration-300"
                  :class="
                    isActiveRoute(item.path)
                      ? 'text-logo-color'
                      : 'text-logo-color/70 group-hover:text-logo-color'
                  "
                >
                  {{ item.name }}
                </span>
              </div>
            </router-link>
          </nav>

          <!-- Mobile Menu Button -->
          <button
            @click="toggleMenu"
            class="md:hidden menu-button relative w-11 h-11 flex items-center justify-center rounded-xl transition-all duration-300"
            :class="menuOpen ? 'bg-white/20' : 'hover:bg-white/10'"
            aria-label="Toggle Menu"
          >
            <div class="hamburger-icon">
              <span
                class="line"
                :class="{ 'rotate-45 translate-y-2': menuOpen }"
              ></span>
              <span
                class="line"
                :class="{ 'opacity-0 scale-x-0': menuOpen }"
              ></span>
              <span
                class="line"
                :class="{ '-rotate-45 -translate-y-2': menuOpen }"
              ></span>
            </div>
          </button>
        </div>
      </div>
    </header>

    <!-- Mobile Navigation Menu -->
    <transition name="mobile-menu">
      <div
        v-if="menuOpen"
        class="fixed inset-0 z-40 md:hidden"
        @click.self="toggleMenu"
      >
        <!-- Backdrop with blur -->
        <div class="absolute inset-0 bg-logo-color backdrop-blur-lg"></div>

        <!-- Menu Content -->
        <div class="relative h-full flex flex-col pt-24 pb-8 px-6">
          <!-- Navigation Links -->
          <nav class="flex-1 space-y-3">
            <router-link
              v-for="(item, index) in navItems"
              :key="item.path"
              :to="item.path"
              @click="toggleMenu"
              class="mobile-nav-link group relative flex items-center space-x-4 p-5 rounded-2xl transition-all duration-300 overflow-hidden"
              :style="{ animationDelay: `${index * 50}ms` }"
            >
              <!-- Background gradient on active -->
              <div
                class="absolute inset-0 transition-all duration-300"
                :class="
                  isActiveRoute(item.path)
                    ? 'bg-white shadow-xl'
                    : 'bg-white/5 group-hover:bg-white/10'
                "
              ></div>

              <!-- Icon Container -->
              <div
                class="relative w-14 h-14 flex items-center justify-center rounded-xl transition-all duration-300 shadow-lg"
                :class="
                  isActiveRoute(item.path)
                    ? 'bg-logo-color text-white'
                    : 'bg-white/10 text-white/70 group-hover:bg-white/20 group-hover:text-white'
                "
              >
                <font-awesome-icon :icon="item.icon" class="w-6 h-6" />
              </div>

              <!-- Text -->
              <div class="relative flex-1">
                <div
                  class="text-lg font-bold transition-all duration-300"
                  :class="
                    isActiveRoute(item.path)
                      ? 'text-logo-color'
                      : 'text-white group-hover:text-white'
                  "
                >
                  {{ item.name }}
                </div>
              </div>

              <!-- Arrow indicator -->
              <div class="relative">
                <font-awesome-icon
                  :icon="['fas', 'arrow-right']"
                  class="w-5 h-5 transform transition-all duration-300"
                  :class="
                    isActiveRoute(item.path)
                      ? 'text-logo-color translate-x-1'
                      : 'text-white/40 group-hover:translate-x-1'
                  "
                />
              </div>
            </router-link>
          </nav>

          <!-- Mobile Menu Footer -->
          <div class="border-t border-white/10 pt-6 space-y-4">
            <div class="text-center space-y-2">
              <div class="text-white font-bold text-lg">Youth Ally</div>
              <div class="text-white/60 text-sm">
                Transforming Youth Leadership
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";

export default {
  name: "ModernNav",
  setup() {
    const menuOpen = ref(false);
    const scrolled = ref(false);
    const route = useRoute();

    const navItems = [
      { name: "Home", path: "/", icon: ["fas", "home"] },
      { name: "About", path: "/about", icon: ["fas", "info-circle"] },
      { name: "Programs", path: "/services", icon: ["fas", "cogs"] },
      {
        name: "Get Involved",
        path: "/getinvolved",
        icon: ["fas", "handshake"],
      },
      { name: "Contact", path: "/contacts", icon: ["fas", "envelope"] },
    ];

    const toggleMenu = () => {
      menuOpen.value = !menuOpen.value;
      document.body.style.overflow = menuOpen.value ? "hidden" : "";
    };

    const isActiveRoute = (path) => {
      return route.path === path;
    };

    const handleScroll = () => {
      scrolled.value = window.scrollY > 20;
    };

    onMounted(() => {
      window.addEventListener("scroll", handleScroll);
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
      document.body.style.overflow = "";
    });

    return {
      menuOpen,
      navItems,
      scrolled,
      toggleMenu,
      isActiveRoute,
    };
  },
};
</script>

<style scoped>
/* Navigation Header */
.nav-header {
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.nav-header.scrolled {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

/* Logo Styling */
.logo-container {
  position: relative;
  display: inline-block;
}

.logo-glow {
  position: absolute;
  inset: -3px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.2),
    rgba(255, 255, 255, 0.05)
  );
  border-radius: 1rem;
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: -1;
  filter: blur(8px);
}

.logo-container:hover .logo-glow {
  opacity: 1;
}

.logo-image {
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.logo-container:hover .logo-image {
  transform: scale(1.08) rotate(-3deg);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

/* Desktop Navigation Links */
.nav-link {
  cursor: pointer;
}

.nav-link span,
.nav-link :deep(svg) {
  color: var(--logo-color, #0d4a9e);
}

/* Mobile Menu Button */
.menu-button {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.hamburger-icon {
  width: 22px;
  height: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.hamburger-icon .line {
  display: block;
  width: 100%;
  height: 2.5px;
  background: var(--logo-color, #0d4a9e);
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
}

/* Mobile Menu Animations */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
}

.mobile-nav-link {
  opacity: 0;
  transform: translateX(-30px);
  animation: slideInLeft 0.4s ease-out forwards;
}

@keyframes slideInLeft {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Prevent horizontal scroll */
.w-full {
  max-width: 100vw;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .brand-text {
    display: none;
  }
}

@media (max-width: 1024px) {
  .brand-text {
    display: none;
  }
}
</style>
